import Box from '@mui/material/Box';

interface LiveRegionProps {
  /** The text to announce. Changing it triggers a new announcement. */
  message: string;
}

/**
 * Announces a change to screen-reader users without moving focus.
 *
 * Needed wherever something happens that a sighted user sees but a screen-reader
 * user would not be told about - for example a list item moving position after a
 * button press.
 *
 * "polite" means the reader finishes its current sentence first rather than
 * interrupting.
 */
export function LiveRegion({ message }: LiveRegionProps) {
  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        /*
         * Visually hidden, but still read by assistive technology.
         *
         * The units matter here, and getting them wrong is invisible until it
         * is not. `width: 1` in MUI's style system means 100%, not one pixel -
         * a number from 0 to 1 is read as a proportion. So this box used to be
         * the full width and height of whatever it was positioned against, and
         * `clip` hid the paint without taking the box out of the layout. On the
         * organizations list that pushed the page 276 pixels wider than the
         * window at desktop sizes, and the whole page slid sideways.
         *
         * `clip-path` is the modern replacement for the deprecated `clip`, and
         * a 1px box with explicit units cannot push anything anywhere.
         */
        position: 'absolute',
        width: '1px',
        height: '1px',
        p: 0,
        m: '-1px',
        overflow: 'hidden',
        clipPath: 'inset(50%)',
        whiteSpace: 'nowrap',
        border: 0,
      }}
    >
      {message}
    </Box>
  );
}
